/*
  Manage game VO
  Only allows for one VO to play at a time, cancelling active VO on new requests
  Dispatches copmplete event passing through a { status, id } object

    - statuses
      - missing   : audip clip was not found, check console for warning logs
      - stopped   : audio clip was interrupted
      - complete  : audio clip played all the way through

*/
export class VoModel extends Phaser.Events.EventEmitter {
  constructor({ config, soundController, enabled }) {
    super();
    this._config = config;
    this._soundController = soundController;
    this._activeAudio = false;
    this._voChain;
    this._voLastPlayed;
    this.enabled = enabled;
  }

  get enabled() {
    return this._enabled;
  }
  set enabled(value) {
    this._enabled = value;
  }

  get isPlaying() {
    return this._activeAudio?.audio && this._activeAudio?.audio.isPlaying;
  }

  voIsPlaying(id) {
    return this._activeAudio?.id == id;
  }

  voWasLastPlayed(id) {
    return this._voLastPlayed == id;
  }

  clearActiveAudioCallback() {
    if (this._activeAudio) {
      this._activeAudio.onComplete = null;
      this._activeAudio.onCompleteScope = null;
    }
  }

  playRecordedVo(audio) {
    this.stop();
    if (!this._enabled) {
      return;
    }
    if (!this._playerAudio) this._playerAudio = document.createElement('audio');
    this._playerAudio.src = audio;
    return this._playerAudio.play();
  }

  playChain(ids, options) {
    const { onComplete, onCompleteScope, priority = 0 } = options ? options : {};
    if (priority < this.voPriority) return false;
    this.stop();
    this.voPriority = priority;
    this._voChain = { vo: [...ids], onComplete, onCompleteScope, priority };
    this._continueVoChain();
  }

  _continueVoChain() {
    if (this._voChain?.vo?.length > 0) {
      const id = this._voChain.vo.shift();
      this._startVo(id, { onComplete: this._continueVoChain, onCompleteScope: this });
    } else if (this._voChain?.onComplete) {
      const { onComplete, onCompleteScope } = this._voChain;
      this._voChain = false;
      onComplete.call(onCompleteScope);
    }
  }

  // play an audio clip, optional complete callbacks
  play(id, options) {
    const { onComplete, onCompleteScope, priority = 0 } = options ? options : {};
    // console.log(id, onComplete, priority);
    if (this._activeAudio?.audio?.isPlaying && priority < this.voPriority) {
      if (onComplete) onComplete.call(onCompleteScope, { type: 'VO', status: 'failed', id });
      return false;
    }
    if (this._activeAudio?.audio?.isPlaying && this._activeAudio.id == id) {
      if (onComplete) onComplete.call(onCompleteScope, { type: 'VO', status: 'failed', id });
      return false;
    }
    this.voPriority = priority;
    // if vo is already running, kill it and dispatch stopped status
    this.stop();
    this._startVo(id, { onComplete, onCompleteScope });
    // return true;
  }

  // stop any active audio
  stop() {
    this._voChain = false;
    if (this._activeAudio) {
      this._soundController.stopVO();
      this.emit('vo_end');
      this._handleVoComplete('stopped');
    }
    if (this._playerAudio) {
      this._playerAudio.pause();
    }
  }

  _startVo(id, options) {
    console.log('_startVo : ', id);
    const { onComplete, onCompleteScope } = options ? options : {};
    const spriteId = this._config[id];

    this._voLastPlayed = id;
    // console.log(
    //   `[VoModel] play | ${spriteId} | ${id} | enabled ${this._enabled} | audioContextLocked`,
    //   this._soundController.audioContextLocked
    // );
    // default to no audio
    let audio = false;
    // check id exists in configuration
    if (!this._soundController.audioContextLocked && spriteId && this._enabled) {
      // grab the configuration and attempt to play the audio
      audio = this._soundController.playVO(spriteId, id);
    }
    if (audio && audio.isPlaying) {
      // if audio is set and is playing, attach event listener to it
      this._activeAudio = { audio, id, onComplete, onCompleteScope };
      audio.once('complete', this._handleActiveAudioComplete, this);
      this.emit('vo_start', { id });
    } else {
      // if audio failed dispatch it as missing
      this._activeAudio = { id, onComplete, onCompleteScope };
      if (JW_WATCH && this._soundController.audioContextLocked) {
        console.warn(`Audio context locked, click the game screen to allow audio`);
      } else if (!this._soundController.audioContextLocked && this._enabled) {
        console.warn(`Cannot play request vo, please check config : ${id}`);
      }
      this._handleVoComplete('missing');
    }
  }

  // internal callback attached to active audio
  _handleActiveAudioComplete() {
    // if this was called then the audio was able to play all the way through
    // console.log(`VoModel._handleActiveAudioComplete`);
    this.emit('vo_end');
    this._handleVoComplete('complete');
  }

  _handleVoComplete(status) {
    // grab configuration of active audio and nullify it, do it early to avoid any callback race conditions

    if (!this._activeAudio) return;
    const { id, audio, onComplete, onCompleteScope } = this._activeAudio;
    this._activeAudio = null;
    // remove any attached listeners
    if (audio) audio.off('complete', this.handleActiveAudioComplete, this);
    // if onshot complete was requested, trigger it
    if (onComplete) onComplete.call(onCompleteScope, { type: 'VO', status, id });
    // dispatch default complete event
    this.emit('vocomplete', { status, id });
  }
}
